/*!
 * @Author: leo
 * @Date: 2019-12-25 20:55:00
 * @Last Modified by: leo
 * @Last Modified time: 2019-12-25 20:55:00
 * @Instruduction: 用户信息状态
 */

import { VuexModule, Module, Action, Mutation, getModule } from 'vuex-module-decorators'
import { getToken, setToken, removeToken, getUserId, setUserId, removeUserId } from '@/common/cookies'
import { resetRouter } from '@/router'
import { UserService, OIDCService } from '@/services/index'
import store from '@/store'

/**
 * OIDC 登录参数接口
 */
interface IOIDCLoginParams {
    code: string   // 授权码
    state: string  // 状态参数
    nonce: string  // 防重放参数
}

export interface IUserState {
    token: string
    userId: string
    name: string
    avatar: string
    role: number
}

@Module({ dynamic: true, store, name: 'user' })
class User extends VuexModule implements IUserState {
    public token = getToken() || ''
    public userId = getUserId() || '' 
    public name = ''
    public avatar = ''
    public role: number = -1

    @Mutation
    private SET_TOKEN(token: string) {
        this.token = token
    }

    @Mutation
    private SET_USER_ID(userId: string) {
        this.userId = userId
    }

    @Mutation
    private SET_NAME(name: string) {
        this.name = name
    }

    @Mutation
    private SET_AVATAR(avatar: string) {
        this.avatar = avatar
    }

    @Mutation
    private SET_ROLE(role: number) {
        this.role = role
    }

    @Action
    public async login(userInfo: { username: string, password: string}) {
        let { username, password } = userInfo
        const params = { username: username.trim(), password: password }
        const result = await UserService.login(params)
        if (result.status === 200) {
            const user = result.data
            setToken(user.token)
            setUserId(user.id)
            this.SET_TOKEN(user.token)
            this.SET_USER_ID(user.id)
        }
        return result
    }

    @Action
    public resetUser() {
        removeToken()
        removeUserId()
        this.SET_TOKEN('')
        this.SET_USER_ID('')
    }

    @Action
    public async getUserInfo() {
        if (this.token === '') {
            throw Error('GetUserInfo: token is undefined!')
        }
        const params = { id: this.userId }
        const result = await UserService.getUserInfo(params)
        if (result.status === 200) {
            const { role, name, avatar_path } = result.data
            this.SET_ROLE(role)
            this.SET_NAME(name)
            this.SET_AVATAR(avatar_path)
        } else {
            throw Error('GetUserInfo failed, please login again.')
        }
    }

    @Action
    public async logOut() {
        if (this.token === '') {
            throw Error('LogOut: token is undefined!')
        }
        removeToken()
        removeUserId()
        resetRouter()

        this.SET_TOKEN('')
        this.SET_USER_ID('')
        this.SET_NAME('')
        this.SET_AVATAR('')
        this.SET_ROLE(-1)
    }

    /**
     * OIDC 登录
     * 将授权码传递给后端交换业务 Token
     * @param params 包含 code、state、nonce 的参数
     * @returns 登录结果
     */
    @Action
    public async oidcLogin(params: IOIDCLoginParams) {
        const result = await OIDCService.exchangeToken(params)
        if (result.status === 200) {
            const user = result.data
            setToken(user.token)
            setUserId(user.id)
            this.SET_TOKEN(user.token)
            this.SET_USER_ID(user.id)
            // 如果后端返回了用户信息，直接设置
            if (user.name) {
                this.SET_NAME(user.name)
            }
            if (user.avatar) {
                this.SET_AVATAR(user.avatar)
            }
            if (user.role !== undefined) {
                this.SET_ROLE(user.role)
            }
        }
        return result
    }

    /**
     * OIDC 登出
     * 调用后端登出接口，清除本地会话，并可选跳转到 IDP 登出页面
     * @returns 登出响应，可能包含 IDP 登出 URL
     */
    @Action
    public async oidcLogout() {
        const result = await OIDCService.logout({ token: this.token })
        
        // 无论后端响应如何，都清除本地会话
        removeToken()
        removeUserId()
        resetRouter()
        this.SET_TOKEN('')
        this.SET_USER_ID('')
        this.SET_NAME('')
        this.SET_AVATAR('')
        this.SET_ROLE(-1)
        
        return result
    }
}

export const UserModule = getModule(User)
